import { useEffect, useRef, useState } from 'react';
import { animate, stagger } from 'animejs';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { createLeaderLineGroup, type LeaderLineConfig } from '../../lib/utils/leaderLines';
import { AlertTriangle, Zap, Eye, Wifi } from 'lucide-react';

export function DetectionSection() {
    const { ref, isInView } = useScrollAnimation<HTMLElement>({ threshold: 0.3 });
    const reducedMotion = useReducedMotion();
    const hasAnimated = useRef(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const [leaderLinesReady, setLeaderLinesReady] = useState(false);

    const features = [
        { icon: Eye, text: 'Holistic Risk Modeling', id: 'feature-0' },
        { icon: Zap, text: 'Rapid Assessment Turnaround', id: 'feature-1' },
        { icon: Wifi, text: 'Digital Twin Integration', id: 'feature-2' },
        { icon: AlertTriangle, text: 'Precision Error Elimination', id: 'feature-3' },
    ];

    // Create leader lines after elements are rendered
    useEffect(() => {
        if (!isInView || reducedMotion || !containerRef.current || !svgRef.current) return;
        if (leaderLinesReady) return;

        // Wait for feature items to be rendered
        setTimeout(() => {
            const detector = document.querySelector('.detector-body');
            if (!detector || !containerRef.current || !svgRef.current) return;

            const lineConfigs: LeaderLineConfig[] = features.map((feature, index) => {
                const featureEl = document.getElementById(feature.id);
                if (!featureEl) return null;

                return {
                    startEl: featureEl,
                    endEl: detector as HTMLElement,
                    startAnchor: 'right' as const,
                    endAnchor: 'left' as const,
                    color: 'var(--neon-cyan)',
                    strokeWidth: 0.5,
                    elbow: true,
                };
            }).filter(Boolean) as LeaderLineConfig[];

            if (lineConfigs.length > 0) {
                createLeaderLineGroup(
                    lineConfigs,
                    containerRef.current,
                    svgRef.current,
                    200 // stagger delay
                );
                setLeaderLinesReady(true);
            }
        }, 1000); // Wait for animations to settle
    }, [isInView, leaderLinesReady, reducedMotion]);

    // Entrance animations
    useEffect(() => {
        if (!isInView || hasAnimated.current || reducedMotion) return;
        hasAnimated.current = true;

        animate('.detection-heading', {
            translateX: [-100, 0],
            opacity: [0, 1],
            duration: 800,
            ease: 'outExpo',
        });

        animate('.detection-text', {
            translateX: [-50, 0],
            opacity: [0, 1],
            duration: 600,
            ease: 'outExpo',
            delay: 200,
        });

        animate('.feature-item', {
            translateX: [-50, 0],
            opacity: [0, 1],
            duration: 500,
            ease: 'outExpo',
            delay: stagger(100, { start: 400 }),
        });

        animate('.detector-body', {
            scale: [0.8, 1],
            opacity: [0, 1],
            duration: 800,
            ease: 'outExpo',
            delay: 200,
        });

        animate('.detection-ring', {
            scale: [0.5, 1.2],
            opacity: [0.8, 0],
            duration: 2000,
            ease: 'out',
            delay: stagger(150),
            loop: true,
        });
    }, [isInView, reducedMotion]);

    return (
        <section
            ref={ref}
            id="detection-section"
            className="py-24 px-4 md:px-8 lg:px-16 relative"
            style={{ background: 'var(--light-bg)' }}
            aria-labelledby="detection-title"
        >
            <div className="max-w-7xl mx-auto relative" ref={containerRef}>
                {/* SVG Leader Lines Overlay */}
                {!reducedMotion && (
                    <svg
                        ref={svgRef}
                        className="absolute inset-0 w-full h-full pointer-events-none hidden lg:block"
                        style={{ zIndex: 5 }}
                        aria-hidden="true"
                    />
                )}

                <div className="grid lg:grid-cols-2 gap-12 items-center relative">
                    <div>
                        <h2
                            id="detection-title"
                            className={`detection-heading text-4xl md:text-5xl font-display font-bold text-white mb-6 ${reducedMotion ? '' : 'opacity-0'}`}
                        >
                            Identify Risks{' '}
                            <span className="text-[var(--fire-orange)]">Before Construction</span>
                        </h2>

                        <p className={`detection-text text-gray-400 text-lg mb-8 ${reducedMotion ? '' : 'opacity-0'}`}>
                            Our analysts detect potential non-compliance and safety hazards in your designs.
                            Using advanced digital twin technology, we ensure your project is code-compliant.
                        </p>

                        <ul className="space-y-4" role="list">
                            {features.map((feature, index) => (
                                <li
                                    key={index}
                                    id={feature.id}
                                    className={`feature-item flex items-center gap-4 ${reducedMotion ? '' : 'opacity-0'}`}
                                >
                                    <div
                                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                                        style={{ background: 'var(--gradient-fire)' }}
                                    >
                                        <feature.icon className="w-5 h-5 text-white" aria-hidden="true" />
                                    </div>
                                    <span className="text-gray-300">{feature.text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="relative flex items-center justify-center">
                        <div
                            className={`detector-body relative w-48 h-48 rounded-full flex items-center justify-center ${reducedMotion ? '' : 'opacity-0'}`}
                            style={{
                                background: 'linear-gradient(135deg, #2a2a2a, #1a1a1a)',
                                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
                            }}
                        >
                            <div
                                className="w-8 h-8 rounded-full animate-glow"
                                style={{
                                    background: 'var(--fire-red)',
                                    boxShadow: '0 0 20px var(--fire-red), 0 0 40px var(--fire-red)'
                                }}
                            />
                        </div>

                        {[1, 2, 3].map((ring) => (
                            <div
                                key={ring}
                                className={`detection-ring absolute rounded-full border-2 ${reducedMotion ? 'hidden' : ''}`}
                                style={{
                                    width: `${200 + ring * 100}px`,
                                    height: `${200 + ring * 100}px`,
                                    borderColor: 'var(--fire-orange)',
                                    opacity: 0,
                                }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default DetectionSection;
